import {
    RiAlignLeft,
    RiCodeSSlashFill,
    RiFontSize, RiH1, RiH2,
    RiImageLine,
    RiLightbulbLine,
    RiQuoteText,
    RiVidiconLine,
} from "@remixicon/react";
import React, { useState } from "react";
import {
    ArticleSection, ArticleSectionType,
} from "../../../__generated__/api";
import AIQuestion, { AvailableActionResponseAction } from "../../../components/artificial-intelligence/AIQuestion";
import Dropdown from "../../../components/theme/dropdown/Dropdown";
import Icon from "../../../components/theme/icon/Icon";
import usePortal from "../../../contexts/PortalContext";
import { santitize } from "../../../components/html-render/HtmlRenderer";
import { Property } from "../../../components/dirty-tracker/FormTracker";
import useKeyboardShortcuts from "../../../contexts/KeyboardShortcutContext";
import { LoggingHandler } from "../../../contexts/logging/LoggingContext";

export interface ActionHandler {
    addSections: (sections: Partial<ArticleSection>[], index?: number) => void;
    deleteSection: (sectionId: string) => void;
    replaceSection: (findSectionId: string, type: ArticleSectionType, section: Partial<ArticleSection>) => void;
    getAllSections: () => ArticleSection[];
}
interface ArticleProperties {
    title: Property<string>,
    iconUrl: Property<string>,
}
const AVAILABLE_ACTIONS = [
    {
        id: "add-sections",
        name: "Add a section or list of sections to the article",
        prompt: "Adds a the provided draftMessages to the article as a new section or sections. "
            + "This should be used when suggesting a section to the user or if the user is asking "
            + "to help them write or draft an article section. "
            + "Try to keep each section not too long and to a single topic."
            + "The message could be in HTML format.",
        parameters: [
            {
                name: "draftMessages",
                description: "An array of draft messages to add to the article in HTML format.",
            },
        ],
        invoke(actions: ActionHandler, response: Record<string, unknown>, _properties: ArticleProperties, index?: number) {
            actions.addSections((response.draftMessages as string[]).map(draftMessage => ({
                type: ArticleSectionType.Html,
                text: {
                    body: {
                        html: santitize(draftMessage),
                    },
                },
            })), index);
        },
    },
    {
        id: "replace-section",
        name: "Replace a section in the article",
        prompt: "Finds the article that contains the provided findSection and replaces it with the replaceSection provided. "
            + "This should be used when suggesting a user wants to find and replace either parts "
            + "or multiple sections of an article. The message could be in HTML format.",
        parameters: [
            {
                name: "findSectionId",
                description: "The id of the section that will be replaced.",
            },
            {
                name: "replaceSection",
                description: "The draft html to replace the section with.",
            },
        ],
        invoke(actions: ActionHandler, response: Record<string, unknown>) {
            actions.replaceSection(response.findSectionId as string, ArticleSectionType.Html, {
                text: {
                    body: {
                        html: santitize(response.replaceSection as string),
                    },
                },
            });
        },
    },
    {
        id: "remove-section",
        name: "Removes a section from the article",
        prompt: "Finds and removes a section from the article given the id (findSectionId) of the section.",
        parameters: [
            {
                name: "findSectionId",
                description: "The id of the section that will be removed.",
            },
        ],
        invoke(actions: ActionHandler, response: Record<string, unknown>) {
            actions.deleteSection(response.findSectionId as string);
        },
    },
    {
        id: "set-title",
        name: "Changes the title of the article",
        prompt: "Changes the title of the article to the one provided. "
            + "This should be suggested when a user is asking to change the title, "
            + "when the article title doesn't match the content or is empty, or when "
            + "they are writing a new article. "
            + "The message could be in HTML format.",
        parameters: [
            {
                name: "newTitle",
                description: "The new title of the article to set.",
            },
        ],
        invoke(actions: ActionHandler, response: Record<string, unknown>, properties: ArticleProperties) {
            properties.title.setValue(response.newTitle as string);
        },
    },

];
export default function InsertMenu({
    actions,
    isPageEmpty,
    properties,
    index,
}: {
    actions: ActionHandler;
    isPageEmpty: boolean;
    properties: ArticleProperties;
    index?: number;
}) {
    const { isAIEnabled } = usePortal();
    const [isShowAI, setShowAI] = useState(false);

    const smartSections = (
        <>
            {isAIEnabled && (
                <Dropdown.Item
                    className="btn-ai-assist"
                    prefixIcon={<Icon.ArtificialIntelligence size="small" />}
                    onClick={() => setShowAI(true)}
                >
                    Write with AI
                </Dropdown.Item>
            )}
            <Dropdown.Item
                prefixIcon={<Icon.Search size="small" />}
                onClick={() => actions.addSections([{
                    type: ArticleSectionType.Search,
                }], index)}
            >
                Search
            </Dropdown.Item>
            <span />
        </>
    );

    useKeyboardShortcuts(shortcuts => {
        if (index == null) {
            shortcuts.on({
                shortcut: "alt+space",
                context: "default",
                handler: () => {
                    setShowAI(true);
                },
            });
        }
    });

    const invokeAction = (action: AvailableActionResponseAction) => {
        AVAILABLE_ACTIONS.find(current => current.id === action.id)?.invoke(
            actions,
            action.parameters,
            properties,
            index,
        );
    };

    const addSections = (sections: Partial<ArticleSection>[], sectionIndex?: number) => {
        const type = sections.firstElement?.type;
        LoggingHandler.instance.metric([LoggingHandler.Metrics.ARTICLES, "sections", "insert", type].filterFalsy());
        actions.addSections(sections, sectionIndex);
    };
    return (
        <>
            <AIQuestion
                onHide={() => setShowAI(false)}
                shouldShow={isShowAI}
                invokeAction={invokeAction}
                availableActions={AVAILABLE_ACTIONS}
                metricPathPrefix={[LoggingHandler.Metrics.ARTICLES]}
                placeholder="Help me write about..."
                additionalContext={`You are a AI bot helping the agent write a help center article.`
                        + `You can use the information provided and any outside information to answer the `
                        + `users question and suggest or write sections of the article. Ultimately, `
                        + `the goal is to help them write an article. \n`
                        + `Your window is overlaying the help center so you only want to be visible when `
                        + `helping the agent. If the agent seems to be done, give them the option to close `
                        + `the AI window so they can continue writing the article. \n`
                        + `If you respond with HTML, only the following HTML tags are allowed any other `
                        + `HTML tags are not allowed at all.  \n`
                        + `a, strong, em, strike, code, pre, h1, h2, ul, ol, li, blockquote, p `
                        + `\n\n`
                        + `# Current Article `
                        + `The current article title: \${properties.title.value} `
                        + `The current article contents in JSON format: \n${
                            JSON.stringify(actions.getAllSections())}`}
            />

            <Dropdown.Menu>

                {isPageEmpty && (
                    <>
                        {smartSections}
                        <Dropdown.Divider />
                    </>
                )}
                <Dropdown.Item
                    onClick={() => addSections([{
                        type: ArticleSectionType.Title,
                        text: { body: { html: "<h1>New Section</h1>" } },
                    }], index)}
                    prefixIcon={<Icon icon={<RiH1 />} size="small" />}
                >
                    Title
                </Dropdown.Item>
                <Dropdown.Item
                    onClick={() => addSections([{
                        type: ArticleSectionType.Title,
                        text: { body: { html: "<h2>New Subtitle</h2>" } },
                    }], index)}
                    prefixIcon={<Icon icon={<RiH2 />} size="small" />}
                >
                    Subtitle
                </Dropdown.Item>
                <Dropdown.Item
                    onClick={() => addSections([{
                        type: ArticleSectionType.Html,
                        text: { body: { html: "<p>New Section</p>" } },
                    }], index)}
                    prefixIcon={<Icon icon={<RiFontSize />} size="small" />}
                >
                    Text
                </Dropdown.Item>
                <Dropdown.Item
                    onClick={() => addSections([{
                        type: ArticleSectionType.Html,
                        text: { body: { html: "<h1>New Title</h1><p>New Section</p>" } },
                    }], index)}
                    prefixIcon={(
                        <Icon
                            icon={<RiAlignLeft />}
                            size="small"
                        />
                    )}
                >
                    Title and Text
                </Dropdown.Item>
                <Dropdown.Item
                    onClick={() => addSections([{
                        type: ArticleSectionType.Html,
                        text: { body: { html: "<blockquote>New Title</blockquote>" } },
                    }], index)}
                    prefixIcon={<Icon icon={<RiQuoteText />} size="small" />}
                >
                    Quote
                </Dropdown.Item>
                <Dropdown.Item
                    prefixIcon={<Icon icon={<RiLightbulbLine />} size="small" />}
                    onClick={() => addSections([{
                        type: ArticleSectionType.Note,
                        text: { body: { html: "<p><strong>💡 Tip:</strong> Here is some help</p>" } },
                    }], index)}
                >
                    Tip
                </Dropdown.Item>
                <Dropdown.Item
                    onClick={() => addSections([{
                        type: ArticleSectionType.Code,
                        text: { body: { html: "<code><pre>My Code</pre></code>" } },
                    }], index)}
                    prefixIcon={<Icon icon={<RiCodeSSlashFill />} size="small" />}
                >
                    Code
                </Dropdown.Item>
                <Dropdown.Item
                    prefixIcon={<Icon icon={<RiImageLine />} size="small" />}
                    onClick={() => addSections([{
                        type: ArticleSectionType.Image,
                        media: { },
                    }], index)}
                >
                    Image
                </Dropdown.Item>
                <Dropdown.Item
                    prefixIcon={<Icon icon={<RiVidiconLine />} size="small" />}
                    onClick={() => addSections([{
                        type: ArticleSectionType.Video,
                        media: { },
                    }], index)}
                >
                    Video
                </Dropdown.Item>

                {!isPageEmpty && (
                    <>
                        <Dropdown.Divider />
                        {smartSections}
                    </>
                )}
            </Dropdown.Menu>
        </>
    );
}

InsertMenu.defaultProps = {
    index: undefined,
};
